import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  $Button,
  $Input,
  $TextArea,
  $Skeleton,
  $Alert,
  $Select,
  $Message,
} from "us.common/components";
import { Formik } from "formik";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import * as Actions from "actions";
import { generateTemplateJSON } from "components/TemplateEditor/Functions";
import { generatePreviewImage } from "functions/TemplateEditor";
import { SaveAsValidationSchema } from "./Validations";
import { RepeatingAdapterChanger } from "components/TemplateEditor/Components/RepeatingAdapterChanger";
import { useLocation } from "react-router-dom";
import { URLParams } from "components/EditorHome/Constants";
import {
  isTemplateNameChanged,
  isValidTemplate,
} from "components/HeaderExtra/Functions";
import * as Azure from "us.helper/azure";
import { getCurrentUserEmail } from "us.helper/utility";


/**
 * @description Save As menu content
 */
const SaveAs: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation();

  const {
    template,
    documentTypes,
    templates,
    saveTemplate,
    AuthorizationProfile,
  } = props;
  const { search } = useLocation();

  const urlParams = new URLSearchParams(search);
  const documentType = urlParams.get(URLParams.DOCUMENT_TYPE);

  const formRef = useRef<any>();
  const { description, templateContent } = template.data ?? {};

  useEffect(() => {
    formRef?.current?.setFieldTouched("templateName", true);
  }, []);
  
  const updatedUser = useMemo(() => {
    const currentUser: any = new Azure.ADAuth().currentUser();
    return getCurrentUserEmail(currentUser, AuthorizationProfile?.authorizationProfile);
  }, [AuthorizationProfile]);
  /**
   * @description - Handle save template button click event
   */
  const handleSubmit = async (values: any) => {
    const templateJson = generateTemplateJSON(template.data);
    if (templateJson && isValidTemplate()) {
      const parsedTemplateJson = JSON.parse(templateJson);

      const { templateName, mainAdapterId, documentType } = values;
      const saveParams = {
        ...parsedTemplateJson,
        templateId: -1,
        templateName,
        description: values?.description,
        outPutFormat: documentType,
        templateContent: {
          ...parsedTemplateJson?.templateContent,
          templateName,
          adapter: {
            ...parsedTemplateJson?.templateContent?.adapter,
            mainAdapterId,
          },
          output: {
            format: documentType,
          },
        },
      };

      try {
        const previewImage = await generatePreviewImage();
        const versionNo = 0;
        saveTemplate &&
          saveTemplate({
            saveParams: {
              ...saveParams,
              templateContent: {
                ...saveParams?.templateContent,
                previewImage,
                versionNo,
                updatedUser,
              },
              isTemplateNameChanged: isTemplateNameChanged(
                template?.data,
                templates?.data
              ),
            },
            isSaveAs: true,
          });
      } catch (e) {
        saveTemplate &&
          saveTemplate({
            saveParams: {
              ...saveParams,
              templateContent: {
                ...saveParams?.templateContent,
                versionNo: 0,
                updatedUser,
              },
              isTemplateNameChanged: false,
            },
            isSaveAs: true,
          });
      }
    } else {
      $Message.error(
        t(
          "US.USC.TEMPLATEEDITOR:VALIDATIONS.PLEASE_SET_UP_THE_REPEATING_ADAPTER_TO_YOUR_TEMPLATE_FOR_YOUR_REPEATING_TOKENS"
        )
      );
    }
  };

  return (
    <$Skeleton
      loading={template?.loading}
      active
      paragraph={{
        rows: 3,
      }}
    >
      <Formik
        initialValues={{
          templateName: template.data?.templateName,
          mainAdapterId: templateContent?.adapter?.mainAdapterId,
          description: description ? description : "",
          documentType,
        }}
        onSubmit={(values: any) => {
          handleSubmit(values);
        }}
        innerRef={formRef}
        enableReinitialize
        validateOnMount
        validateOnChange
        validateOnBlur
        validationSchema={SaveAsValidationSchema(template.data?.templateName)}
      >
        {({ values, handleSubmit, isValid }: any) => (
          <>
            <div className="mb-2">
              <div className="ant-form-item-required">
                {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.TEMPLATE_NAME")}
              </div>
              <$Input name="templateName" size="small" className="w-100" />
            </div>
            <div className="mb-2">
              <div className="ant-form-item-required">
                {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADAPTER")}
              </div>
              <RepeatingAdapterChanger
                name="mainAdapterId"
                isRepeating={false}
                isSelect={true}
              />
              {templateContent?.adapter?.mainAdapterId !=
                values?.mainAdapterId && (
                <$Alert
                  message={t(
                    "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.THIS_MAY_NEED_TO_CHANGE_SOME_PARAMETERS_IN_THE_TEMPLATE"
                  )}
                  type="warning"
                  showIcon
                  className="error-message mt-3"
                />
              )}
            </div>
            <div className="mb-3">
              <div className="">
                {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.DESCRIPTION")}
              </div>
              <$TextArea name="description" style={{ width: "100%" }} />
            </div>
            <div className="mb-2">
              <$Select
                name="documentType"
                formitem={{
                  label: t(
                    "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.DOCUMENT_FORMAT"
                  ),
                }}
                size="small"
                className="mr-2"
                loading={documentTypes.loading}
                disabled={documentTypes.loading}
                options={documentTypes.data}
                optionText={"channelDisplayName"}
                optionValue={"channelCode"}
                showSearch={false}
                allOption={false}
                dropdownMatchSelectWidth={false}
                style={{ width: "100px" }}
              />
            </div>
            <div className="d-flex flex-fill justify-content-end">
              <$Button
                type="primary"
                size="small"
                className="w-100"
                disabled={!isValid || template?.saving}
                loading={template?.saving ? true : false}
                onClick={handleSubmit}
              >
                {t("US.COLLECTION.COMMON:COMMON.SAVE")}
              </$Button>
            </div>
          </>
        )}
      </Formik>
    </$Skeleton>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templatesHistory, AuthorizationProfile } = state;
  const { template, documentTypes, templates, templateVersions } =
    templatesHistory;

  return {
    template,
    documentTypes,
    templates,
    templateVersions,
    AuthorizationProfile,
  };
};

const { templateData } = Actions.templatesHistory;

const mapDispatchToProps = {
  saveTemplate: templateData.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(SaveAs);
